import { Controller } from "@hotwired/stimulus"
import { createSeriesMarkers } from "lightweight-charts"
import { createCandleChart, UP, DOWN } from "../lib/candle_chart"
import { subscribeToCandles } from "../channels/candles_subscription"
import { subscribeToBot } from "../channels/bot_subscription"

const PAGE_SIZE = 1000

// Renders the bot's pair candles for its timeframe with buy/sell markers
// overlaid from the bot's positions. Live candles come from CandlesChannel;
// live markers from BotChannel.
export default class extends Controller {
  static targets = ["container"]
  static values = {
    pairId: Number,
    botId: Number,
    timeframe: { type: String, default: "1h" }
  }

  connect() {
    const { chart, candleSeries, volumeSeries } = createCandleChart(this.containerTarget)
    this.chart = chart
    this.candleSeries = candleSeries
    this.volumeSeries = volumeSeries
    // lightweight-charts v5: markers are a series primitive, not setMarkers().
    this.seriesMarkers = createSeriesMarkers(this.candleSeries, [])
    this.bars = []

    this.loadCandles()
    this.loadMarkers()
    this.subscribe()
  }

  disconnect() {
    if (this.candleSub) this.candleSub.unsubscribe()
    if (this.botSub) this.botSub.unsubscribe()
    if (this.chart) {
      this.chart.remove()
      this.chart = null
    }
  }

  async loadCandles() {
    const url = new URL(
      `/trading_pairs/${this.pairIdValue}/candles.json`,
      window.location.origin
    )
    url.searchParams.set("timeframe", this.timeframeValue)
    url.searchParams.set("limit", String(PAGE_SIZE))

    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`candles request failed: ${response.status}`)
      const data = await response.json()
      this.bars = data.candles
      this.renderBars()
      this.chart.timeScale().fitContent()
    } catch (error) {
      console.warn("bot-chart: failed to load candles", error)
    }
  }

  renderBars() {
    this.candleSeries.setData(
      this.bars.map((c) => ({
        time: c.time, open: c.open, high: c.high, low: c.low, close: c.close
      }))
    )
    this.volumeSeries.setData(
      this.bars.map((c) => ({
        time: c.time, value: c.volume, color: c.close >= c.open ? UP : DOWN
      }))
    )
  }

  async loadMarkers() {
    try {
      const response = await fetch(`/bots/${this.botIdValue}/positions.json`)
      if (!response.ok) throw new Error(`positions request failed: ${response.status}`)
      const data = await response.json()
      this.seriesMarkers.setMarkers(data.markers || [])
    } catch (error) {
      console.warn("bot-chart: failed to load positions", error)
    }
  }

  subscribe() {
    this.candleSub = subscribeToCandles({
      pairId: this.pairIdValue,
      timeframe: this.timeframeValue,
      onUpdate: (data) => this.applyLiveCandles(data.candles)
    })
    this.botSub = subscribeToBot({
      botId: this.botIdValue,
      onUpdate: (data) => {
        if (data.markers) this.seriesMarkers.setMarkers(data.markers)
      }
    })
  }

  // Merges pushed candles into the live series, keeping `this.bars` in sync.
  applyLiveCandles(candles) {
    candles.forEach((c) => {
      const last = this.bars[this.bars.length - 1]
      if (last && c.time < last.time) return

      if (last && c.time === last.time) {
        this.bars[this.bars.length - 1] = c
      } else {
        this.bars.push(c)
      }

      this.candleSeries.update({
        time: c.time, open: c.open, high: c.high, low: c.low, close: c.close
      })
      this.volumeSeries.update({
        time: c.time, value: c.volume, color: c.close >= c.open ? UP : DOWN
      })
    })
  }
}
